/**
 * Client-side route gate.
 *
 * The authoritative gate lives in `server/middleware/auth.ts` (Nitro/
 * Worker): SSR-protected routes 302 to `/bff/auth/login` before any
 * page renderer runs. This client middleware exists to short-circuit
 * SPA navigations so the user doesn't trip the server gate for every
 * client-side route change.
 *
 * Source of truth for "am I signed in" is the BFF, not the in-memory
 * `useAuth` state — the post-OIDC-callback hard nav races plugin setup,
 * so relying on `plugins/auth.client.ts` to have hydrated `user` before
 * this middleware fires would reintroduce a reload loop. When
 * `user.value` is empty we ask `/bff/auth/user` directly here.
 */
import { hydrateAuthFromMe } from "~/composables/useAuth";
import { getAuthRules } from "~/composables/useNavConfig";

export default defineNuxtRouteMiddleware(async (to, from) => {
  if (import.meta.server) return;

  const rules = await getAuthRules();
  const rule = rules[to.path];
  if (!rule) return;

  const { evaluateRule, isAuthenticated, user, setUser } = useAuth();

  // Lazy-hydrate from the BFF when local state hasn't caught up. The
  // BFF cookie is HttpOnly so this is the only way to read session
  // state on the client.
  if (!user.value) await hydrateAuthFromMe(setUser);

  if (evaluateRule(rule)) return;

  // Authenticated but rule-mismatched (e.g. logged-in user without `admin`).
  // Re-running the IdP flow won't grant the missing role, so bounce to the
  // locale home and surface a toast.
  if (isAuthenticated.value) {
    const raw = (to.params.locale as string | undefined) ?? "";
    const locale = raw === "en" || raw === "nl" ? raw : "en";
    if (import.meta.dev) console.warn(`[auth] Forbidden: route=${to.path}`);
    const copy: Record<string, { title: string; description: string }> = {
      en: {
        title: "Access denied",
        description: "Your account doesn't have permission to view that page.",
      },
      nl: {
        title: "Geen toegang",
        description: "Je account heeft geen rechten voor deze pagina.",
      },
    };
    const msg = copy[locale] ?? copy.en!;
    document.dispatchEvent(
      new CustomEvent("e-toast:create", {
        detail: {
          id: `auth-forbidden-${Date.now()}`,
          type: "warning",
          title: msg.title,
          description: msg.description,
        },
      }),
    );
    return navigateTo(`/${locale}`);
  }

  // Unauthenticated → SPA-redirect to the public sign-in interstitial (not
  // straight to the IdP), so the visitor can confirm or go back before the
  // OIDC round trip. The interstitial's "Sign in" button does the hard nav to
  // `/bff/auth/login` — with its own circuit breaker (see `useAuth.signIn`) —
  // so no loop-guard is needed here: this branch no longer auto-redirects to
  // the IdP. `replace` keeps the interstitial out of history so its "Go back"
  // returns to the page the visitor came from.
  const rawLocale = (to.params.locale as string | undefined) ?? "";
  const locale = rawLocale === "nl" ? "nl" : "en";
  // Carry the page we came from so the interstitial's "Go back" / backdrop
  // dismiss returns there deterministically. `from` is a real prior route only
  // when it differs from the blocked target (a direct/cold entry has `from ===
  // to`); in that case omit it and let the interstitial fall back to home.
  const back = from.fullPath !== to.fullPath ? from.fullPath : "";
  const q =
    `returnUrl=${encodeURIComponent(to.fullPath)}` +
    (back ? `&back=${encodeURIComponent(back)}` : "");
  return navigateTo(`/${locale}/sign-in-required?${q}`, { replace: true });
});
